import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {QueryFieldDrawer} from './QueryFieldDrawer';
import {t} from 'i18next';
import ECATagTab from '../../../eca/ECATagTab';
import {Box, Typography} from '@mui/material';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../eca-doc-list/eca-save-search/EcaSaveSearch';
import {getUTCDateTimeForDisplay} from '../../../../utils/utils';
import {isEmpty, orderBy} from 'lodash';
import {AurLightTooltip} from '../../../common/aur-light-tooltip/aur-light-tooltip';

interface IQueryFieldTagDrawerProps {
  open: boolean;
  defaultSelectedTagIds?: string;
  onCancel?: () => void;
  onApply?: (tagData?: ExtendedTreeItemProps[] | null) => void;
}

const QueryFieldTagDrawer: React.FC<IQueryFieldTagDrawerProps> = ({
  open,
  defaultSelectedTagIds,
  onCancel,
  onApply,
}) => {
  const [isModal, setIsModal] = useState<boolean>(open);
  const [searchTerm, setSearchTerm] = useState<string>();
  const [selectedTag, setSelectedTag] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[] | null
  >();
  const [selectedFolder, setSelectedFolder] =
    useState<TreeViewBaseItem<ExtendedTreeItemProps> | null>();

  useEffect(() => {
    setIsModal(open);
  }, [open]);

  const onCancelClicked = useCallback(() => {
    setIsModal(false);
    onCancel?.();
  }, [onCancel]);

  const onApplyClicked = useCallback(() => {
    setIsModal(false);
    onApply?.(selectedTag);
  }, [onApply, selectedTag]);

  const onSearch = useCallback((search?: string) => {
    setSearchTerm(search);
    setSelectedTag(undefined);
  }, []);

  const onTagsSelected = useCallback((tags?: ExtendedTreeItemProps[]) => {
    setSelectedTag(tags);
  }, []);

  const onTagSelected = useCallback(
    (tagInfo?: ExtendedTreeItemProps) => {
      if (!tagInfo) return;

      const firstItem = selectedTag?.[0];
      const isFolder = firstItem?.fileType === 'folder';
      const isDifferentField = firstItem?.fieldId !== tagInfo.fieldId;

      if (isFolder || isDifferentField) {
        setSelectedTag([tagInfo]);
        return;
      }

      const alreadySelected = selectedTag?.some(
        (item) => item.id === tagInfo.id
      );

      if (alreadySelected) {
        const updatedTags =
          selectedTag?.filter((item) => item.id !== tagInfo.id) || [];

        if (updatedTags.length === 0) {
          setSelectedFolder(undefined);
        }

        setSelectedTag(updatedTags);
      } else {
        const newSelected = [...(selectedTag || []), tagInfo];
        setSelectedTag(newSelected);
      }
    },
    [selectedTag]
  );

  const onTagFolderSelected = useCallback(
    (folderInfo?: ExtendedTreeItemProps) => {
      const firstItem =
        selectedTag && selectedTag?.length > 0 ? selectedTag[0] : undefined;
      if (
        firstItem?.fileType !== 'folder' ||
        folderInfo?.id !== selectedFolder?.id
      ) {
        setSelectedFolder(folderInfo);
        folderInfo && setSelectedTag([folderInfo]);
      } else if (folderInfo?.id === selectedFolder?.id) {
        setSelectedFolder(undefined);
        setSelectedTag([]);
      }
    },
    [selectedFolder?.id, selectedTag]
  );

  const selectedIds = useMemo(() => {
    const firstItem =
      selectedTag && selectedTag?.length > 0 ? selectedTag[0] : undefined;
    if (firstItem?.fileType === 'folder') {
      return [firstItem.id];
    } else {
      return selectedTag?.map((item) => {
        return item.id;
      });
    }
  }, [selectedTag]);

  const defaultIds = useMemo(
    () => defaultSelectedTagIds?.split('##'),
    [defaultSelectedTagIds]
  );

  const folderSelected = useMemo(
    () => selectedTag?.[0]?.fileType === 'folder',
    [selectedTag]
  );

  const renderTagFieldInfo = useCallback(() => {
    return (
      <Box className={'selected-tag-text-container'} flex={1} gap={'16px'}>
        <Typography className="typo-text subhead3_size color-mist-1300">
          {selectedFolder?.label}
        </Typography>
        <Typography className="typo-text body3_size color-mist-1400">
          {t('indexSearchPage.tagDrawer.folderSelectedSummary')}
        </Typography>
      </Box>
    );
  }, [selectedFolder?.label]);

  const renderSelectedTags = useCallback(() => {
    return (
      <>
        <Box className={'selected-tag-text-container'}>
          <Typography className="typo-text subhead3_size color-mist-100">
            {t('indexSearchPage.tagDrawer.selectedTags')}
          </Typography>
        </Box>
        {orderBy(selectedTag ?? [], ['displayOrder'], ['desc']).map(
          (tagItem, index) => {
            return (
              <Box className="tag-info-view" key={index * 1}>
                <Typography
                  aria-label={`${tagItem?.label} ${t('indexSearchPage.created')} ${tagItem?.createdBy ?? ''} ${getUTCDateTimeForDisplay(tagItem.createdDate)}`}
                  className="typo-text subhead3_size color-mist-100"
                >
                  {tagItem?.label}
                </Typography>
                <Typography className="typo-text label1_size color-mist-100">
                  {`${tagItem.count} total docs when last run`}
                </Typography>
                <Box className={'create-info'}>
                  <Typography
                    aria-hidden="true"
                    className="typo-text body4_size color-mist-400"
                  >
                    {t('indexSearchPage.created')}
                  </Typography>
                  <Box
                    aria-hidden="true"
                    display={'flex'}
                    flexDirection={'column'}
                  >
                    <Typography
                      aria-hidden="true"
                      className="typo-text body4_size color-mist-400"
                    >
                      {tagItem?.createdBy ?? ''}
                    </Typography>
                    <AurLightTooltip title={t('common.yearMonthDate')}>
                      <Typography
                        aria-hidden="true"
                        className="typo-text body4_size color-mist-400"
                      >
                        {getUTCDateTimeForDisplay(tagItem?.createdDate)}
                      </Typography>
                    </AurLightTooltip>
                  </Box>
                </Box>
              </Box>
            );
          }
        )}
      </>
    );
  }, [selectedTag]);

  const selectedTagContentInfo = useMemo(() => {
    if (folderSelected) {
      return renderTagFieldInfo();
    }

    if (isEmpty(selectedTag)) {
      return (
        <Box className="selected-tag-text-container">
          <Typography className="typo-text body3_size summary-text">
            {t('indexSearchPage.tagDrawer.folderSelectedSummary')}
          </Typography>
        </Box>
      );
    }

    return renderSelectedTags();
  }, [folderSelected, renderSelectedTags, renderTagFieldInfo, selectedTag]);

  const renderRightPanel = useCallback(() => {
    return (
      <Box className="tag-info-container">
        <Box aria-hidden="true" className={'tag-divider'} />
        <Box aria-hidden="true" className={'tag-children-container'}>
          {selectedTagContentInfo}
        </Box>
      </Box>
    );
  }, [selectedTagContentInfo]);

  const renderTags = useMemo(() => {
    return (
      <Box className={'query-tag-field-container'}>
        <Box className={'eca-tag-tab-container'}>
          <ECATagTab
            isShowSearch={false}
            isShowFlyout={false}
            searchProps={{value: searchTerm}}
            isShowCountLabel={false}
            defaultSelectedTagIds={defaultIds}
            selectedItems={selectedIds}
            multipleSelection={true}
            multipleSelectedLabel={
              folderSelected
                ? t('indexSearchPage.tagDrawer.fieldSelected')
                : t('indexSearchPage.tagDrawer.tagsSelected', {
                    count: selectedTag?.length ?? 0,
                  })
            }
            onTagsSelected={onTagsSelected}
            onTagSelected={onTagSelected}
            onTagFolderSelected={onTagFolderSelected}
          />
        </Box>
        {renderRightPanel()}
      </Box>
    );
  }, [
    defaultIds,
    folderSelected,
    onTagFolderSelected,
    onTagSelected,
    onTagsSelected,
    renderRightPanel,
    searchTerm,
    selectedIds,
    selectedTag?.length,
  ]);

  return (
    <QueryFieldDrawer
      open={isModal}
      headerProps={{
        title: t('indexSearchPage.tagDrawer.tags'),
        applyTitle:
          folderSelected || isEmpty(selectedIds)
            ? t('indexSearchPage.applyDrawer')
            : t('indexSearchPage.tagDrawer.applyTagsSelected', {
                count: selectedTag?.length,
              }),
        isApplyDisabled: isEmpty(selectedIds),
        onCancelClicked,
        onApplyClicked,
      }}
      searchProps={{
        title: t('indexSearchPage.tagDrawer.searchTitle'),
        onSearch,
      }}
    >
      {renderTags}
    </QueryFieldDrawer>
  );
};

export {QueryFieldTagDrawer};
